import { useEffect, useState } from 'react'
import Keycloak, { KeycloakInitOptions, KeycloakPkceMethod } from 'keycloak-js'
import { IKeycloakContext } from './keycloakContext'

export interface IKeycloak {
    dev: IKeycloakConfig
    staging: IKeycloakConfig
    prod: IKeycloakConfig
}

interface IKeycloakConfig {
    realm: string
    url: string
    clientId: string
}

const TOKEN_KEY = 'react-token'
const REFRESH_TOKEN_KEY = 'react-refresh-token'

export const useKeycloak = (
    keycloakConfig: IKeycloak | undefined,
    options?: KeycloakInitOptions,
    env = process.env.REACT_APP_ENV_NAME as 'dev' | 'staging' | 'prod',
    tokenName = 'preferred_username',
    tokenMinValidityTime = 60,
    token_refresh_interval = 5 * 60 * 1000,
) => {
    const [keycloakOptions, setKeycloakOptions] = useState<IKeycloakContext>({
        isAuthenticated: false,
        isAuthenticating: true,
    })

    useEffect(() => {
        if (!keycloakConfig) {
            return
        }

        const keycloak = new Keycloak({ ...keycloakConfig[env] })

        const token = localStorage.getItem(TOKEN_KEY)
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
        const otherConfig = {
            ...options,
            pkceMethod: 'S256' as KeycloakPkceMethod,
        }
        const initConfig =
            token && refreshToken
                ? { ...otherConfig, token, refreshToken }
                : { ...otherConfig }

        setKeycloakOptions(kco => ({
            ...kco,
            logout: async () => {
                removeTokens(setKeycloakOptions)
                await keycloak.logout({ redirectUri: window.location.href })
            },
            login: keycloak.login,
            register: keycloak.register,
            error: undefined,
            keycloak,
        }))

        keycloak
            .init(initConfig)
            .then((authenticated: any) => {
                if (!authenticated) {
                    setKeycloakOptions(kco => ({
                        ...kco,
                        isAuthenticating: false,
                    }))
                    return
                }

                setKeycloakOptions(kco => ({
                    ...kco,
                    isAuthenticated: true,
                    isAuthenticating: false,
                    name: keycloak.tokenParsed && keycloak.tokenParsed[tokenName],
                    roles: keycloak?.tokenParsed?.realm_access?.roles,
                }))

                setTokens(
                    keycloak.token as string,
                    keycloak.refreshToken as string,
                    setKeycloakOptions,
                )

                const refreshTokenFn = () => {
                    if (keycloak.isTokenExpired(tokenMinValidityTime)) {
                        keycloak
                            .updateToken(tokenMinValidityTime)
                            .then(() => {
                                setTokens(
                                    keycloak.token as string,
                                    keycloak.refreshToken as string,
                                    setKeycloakOptions,
                                )
                            })
                            .catch((e: any) => {
                                removeTokens(setKeycloakOptions)
                                setKeycloakOptions(kco => ({
                                    ...kco,
                                    error: e || 'Failed to refresh token',
                                    isAuthenticated: false,
                                }))
                            })
                    }
                }

                refreshTokenFn() // Call the function once to try and refresh the token immediately

                // Set up a timer to periodically refresh the token if it needs it
                const tokenRefreshTimer = setInterval(() => {
                    refreshTokenFn()
                }, token_refresh_interval)

                // Clean up the timer when the component unmounts
                return () => clearInterval(tokenRefreshTimer)
            })
            .catch((e: any) => {
                removeTokens(setKeycloakOptions)
                keycloak.clearToken()
                setKeycloakOptions(kco => ({
                    ...kco,
                    isAuthenticating: false,
                    error: e || 'Failed to initialize Keycloak',
                }))
            })
    }, [keycloakConfig])

    return keycloakOptions
}

const removeTokens = (
    setKeycloakOptions: React.Dispatch<React.SetStateAction<IKeycloakContext>>,
) => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    setKeycloakOptions(kco => ({
        ...kco,
        isAuthenticated: false,
        token: undefined,
    }))
}

const setTokens = (
    token: string,
    refreshToken: string,
    setKeycloakOptions: React.Dispatch<React.SetStateAction<IKeycloakContext>>,
) => {
    setKeycloakOptions(kco => ({ ...kco, token, error: undefined }))
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}
